import React from "react";
import { Link } from "react-router-dom";
import { formatINR, calcDiscountPct, getProductCategories } from "../../lib/utils-cynos";
import WishlistButton from "../common/WishlistButton";


const Badge = ({ label, tone = "light" }) => (
  <span
    className={`rounded-[3px] px-2 py-1 font-mono text-[8px] uppercase tracking-[0.14em] backdrop-blur md:text-[12px] ${
      tone === "red" ? "bg-cynos-red text-white" : "bg-black/70 text-white"
    }`}
  >
    {label}
  </span>
);

const mainBadgeFor = (product, isComingSoon) => {
  if (isComingSoon) return "Coming Soon";
  return product.badges?.find((badge) => /new|best|limited|trending/i.test(badge))
    || product.badges?.[0]
    || getProductCategories(product)[0]
    || "";
};



const ProductCard = ({ product }) => {
  const pct = calcDiscountPct(product.price, product.original_price);
  const isInactive = Number(product.active_status) === 0;
  const isComingSoon = Number(product.active_status) === 2;

  const productUrl = `/product/${product.seo_slug || product.slug}`;
  const primaryImage = product.images?.[0] || product.image;
  const secondaryImage = product.images?.[1];
  const mainBadge = mainBadgeFor(product, isComingSoon);



  return (
    <article
      className="group flex flex-col overflow-hidden rounded-[8px] border border-white/10 bg-[#101010] transition duration-300 hover:-translate-y-0.5 hover:border-white/25 hover:shadow-[0_18px_44px_rgba(0,0,0,0.35)]"
      data-testid={`product-card-${product.slug}`}
    >
      {/* Image section: ~78% of card */}
      <div className="relative overflow-hidden bg-cynos-surface">
        <Link
          to={productUrl}
          className="block aspect-[4/6] overflow-hidden md:aspect-[4/5]"
          aria-label={`View ${product.name}`}
        >
          {primaryImage ? (
            <img
              src={primaryImage}
              alt={product.name}
              className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.06]"
              loading="lazy"
              decoding="async"
              sizes="(max-width: 480px) 100vw, (max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-cynos-surface font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              No image
            </div>
          )}
          {secondaryImage && (
            <img
              src={secondaryImage}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-top opacity-0 transition duration-700 ease-out group-hover:scale-105 group-hover:opacity-100"
              loading="lazy"
              decoding="async"
              sizes="(max-width: 480px) 100vw, (max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
            />
          )}
        </Link>

        <div className="absolute left-2 top-2 flex max-w-[calc(100%-64px)] flex-wrap gap-1.5 md:left-3 md:top-3">
          {mainBadge && <Badge label={mainBadge} tone={isComingSoon ? "red" : "light"} />}
          {pct > 0 && <Badge label={`${pct}% Off`} tone="red" />}
        </div>


        <WishlistButton product={product} className="absolute right-2 top-2 h-9 w-9 rounded-[8px] bg-black/75 md:right-3 md:top-3 md:h-10 md:w-10" />
      </div>

      {/* Details section: ~22% of card - compact */}
      <div className="flex flex-col px-2.5 py-2 md:px-3 md:py-2.5">
        <h3 className="truncate text-[11px] font-extrabold leading-tight text-white md:text-[13px]">
          {product.name}
        </h3>
        <div className="mt-0.5 flex items-baseline gap-1.5 md:mt-1">
          <span className="text-[12px] font-extrabold text-white md:text-[14px]">
            {formatINR(product.price)}
          </span>
          {product.original_price > product.price && (
            <span className="text-[10px] text-zinc-500 line-through md:text-[11px]">
              {formatINR(product.original_price)}
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default React.memo(ProductCard);
